import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { LangProvider } from "./i18n/LangContext";
import Lenis from "lenis";
import "./App.css";
import ErrorBoundary from "./components/ErrorBoundary";
import Seo from "./components/Seo";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { trackPageView } from "./lib/analytics";
import { captureLeadSource } from "./lib/leadSource";

// Remember the visitor's first landing (AI assistant, search, social, campaign, direct)
// once per session — every lead form attaches it so the admin shows where leads came from.
captureLeadSource();
import Home from "./pages/Home";
import PreConstructionFinance from "./pages/PreConstructionFinance";
import Apply from "./pages/Apply";
import Brokers from "./pages/Brokers";
import BrokerSubmit from "./pages/BrokerSubmit";
import ExpertNetwork from "./pages/ExpertNetwork";
import Investors from "./pages/Investors";
import InvestorInfo from "./pages/InvestorInfo";
import DevelopmentFinance from "./pages/DevelopmentFinance";
import AdminInbox from "./pages/AdminInbox";
import LandingPage from "./pages/LandingPage";
import Insights from "./pages/Insights";
import ArticlePage from "./pages/ArticlePage";
import { ARTICLE_META } from "./content/articles";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Book from "./pages/Book";
import Legal from "./pages/Legal";
import Faq from "./pages/Faq";
import Sitemap from "./pages/Sitemap";

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    trackPageView(pathname + (hash || ""));
    if (hash) {
      const t = setTimeout(() => {
        document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
      }, 400);
      return () => clearTimeout(t);
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function SmoothScroll() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);
  return null;
}

function RouteMeta() {
  const { pathname: rawPath } = useLocation();
  const pathname = rawPath === "/" ? "/" : rawPath.replace(/\/+$/, "") || "/";
  const lang = pathname.startsWith("/pt")
    ? "pt"
    : pathname.startsWith("/nl")
      ? "nl"
      : "en";
  const base = lang === "en" ? pathname : pathname.replace(/^\/(pt|nl)/, "") || "/";
  const ROUTE_META = {
    "/": {
      title: "Verde Capital | Senior-Secured Development Finance in Portugal",
      description:
        "Short-term, senior-secured development finance for experienced developers in Portugal — land acquisition, licensing and pre-sales, with indicative terms within days.",
      ptTitle: "Verde Capital | Financiamento ao desenvolvimento em Portugal",
      ptDescription:
        "Financiamento de curto prazo com garantia sénior para promotores experientes em Portugal — da aquisição de terreno ao licenciamento e pré-vendas, com condições indicativas em dias.",
      nlTitle: "Verde Capital | Snelle ontwikkelfinanciering in Portugal",
      nlDescription:
        "Kortlopende ontwikkelingsfinanciering met senior zekerheid voor ervaren ontwikkelaars in Portugal — grondverwerving, vergunningen en voorverkoop, met indicatieve voorwaarden binnen enkele dagen.",
    },
    "/development-finance-portugal": {
      title: "Development Finance in Portugal | Verde Capital",
      description:
        "Short-term, senior-secured development finance of €1–5m for experienced developers — land acquisition, licensing and pre-sales before bank financing.",
      ptTitle: "Financiamento ao Desenvolvimento em Portugal | Verde Capital",
      ptDescription:
        "Financiamento de curto prazo com garantia sénior de €1–5m para promotores experientes — aquisição de terreno, licenciamento e pré-vendas antes do crédito bancário.",
      nlTitle: "Ontwikkelfinanciering in Portugal | Verde Capital",
      nlDescription:
        "Kortlopende ontwikkelingsfinanciering van €1–5 mln met senior zekerheid voor ervaren ontwikkelaars — grondverwerving, vergunningen en voorverkoop vóór de bankfinanciering.",
    },
    "/insights": {
      title: "Development Finance Insights | Verde Capital",
      description:
        "Educational guides on financing Portuguese residential development — stages, security, exits and risks, written by the Verde Capital credit team.",
      ptTitle: "Informação sobre Financiamento ao Desenvolvimento | Verde Capital",
      ptDescription:
        "Recursos educativos sobre o financiamento do desenvolvimento residencial em Portugal — fases, garantias, saídas e riscos, pela equipa de crédito da Verde Capital.",
      nlTitle: "Inzichten ontwikkelfinanciering | Verde Capital",
      nlDescription:
        "Educatieve gidsen over financiering van residentiële ontwikkeling in Portugal — fasen, zekerheden, exits en risico's, door het kredietteam van Verde Capital.",
    },
    "/products/pre-construction-finance": {
      title: "Pre-construction Finance in Portugal | Verde Capital",
      description:
        "Senior-secured financing for experienced developers — land acquisition, licensing and pre-sale bridge loans of €1–5m across every residential living category.",
      ptTitle: "Financiamento à Pré-Construção em Portugal | Verde Capital",
      ptDescription:
        "Crédito com garantia sénior para promotores experientes — aquisição de terreno, licenciamento e ponte para pré-vendas de €1–5m em todas as categorias habitacionais.",
      nlTitle: "Voorbouwfase financiering in Portugal | Verde Capital",
      nlDescription:
        "Financiering met senior zekerheid voor ervaren ontwikkelaars — grondverwerving, vergunningen en brugfinanciering voor voorverkoop van €1–5 mln in alle wooncategorieën.",
    },
    "/apply": {
      title: "Get Indicative Terms | Verde Capital",
      description:
        "Describe your project to Verde, our AI underwriter. It asks what's missing, builds your case, and we send indicative terms within 24 hours.",
      ptTitle: "Obtenha Condições Indicativas | Verde Capital",
      ptDescription:
        "Descreva o seu projeto à Verde, a nossa subscritora de IA. Ela pede o que falta, constrói o seu processo e enviamos condições indicativas em 24 horas.",
      nlTitle: "Indicatieve voorwaarden aanvragen | Verde Capital",
      nlDescription:
        "Beschrijf uw project aan Verde, onze AI-underwriter. Zij vraagt wat ontbreekt, bouwt uw dossier op en wij sturen indicatieve voorwaarden binnen 24 uur.",
    },
    "/investor-info": {
      title: "Request Investor Information | Verde Capital",
      description:
        "Request investor information — 8% target annual yield, paid quarterly, senior-secured loans to experienced Portuguese developers.",
      ptTitle: "Pedir Informação para Investidores | Verde Capital",
      ptDescription:
        "Peça informação para investidores — rentabilidade anual alvo de 8%, paga trimestralmente, créditos com garantia sénior a promotores experientes em Portugal.",
      nlTitle: "Investeerdersinformatie aanvragen | Verde Capital",
      nlDescription:
        "Vraag investeerdersinformatie aan — 8% streefrendement per jaar, per kwartaal uitgekeerd, senior gedekte leningen aan ervaren ontwikkelaars in Portugal.",
    },
    "/brokers": {
      title: "Brokers and Advisers | Verde Capital",
      description:
        "Earn 1% of the funded amount — 1.2% on your second deal, 1.5% from your third. Introduce residential development opportunities; Verde pays you, not the developer.",
      ptTitle: "Intermediários e Consultores | Verde Capital",
      ptDescription:
        "Ganhe 1% do montante financiado — 1,2% no segundo negócio, 1,5% a partir do terceiro. Apresente oportunidades de desenvolvimento; a Verde paga-lhe a si, não ao promotor.",
      nlTitle: "Tussenpersonen en adviseurs | Verde Capital",
      nlDescription:
        "Ontvang 1% van het gefinancierde bedrag — 1,2% bij uw tweede deal, 1,5% vanaf uw derde. Dien woningbouwontwikkelingen in; de Verde betaalt u, niet de ontwikkelaar.",
    },
    "/brokers/submit": {
      title: "Submit a Deal | Verde Capital",
      description:
        "Send a residential development opportunity straight to the Verde Capital team. We underwrite, structure and fund — you earn the commission.",
      ptTitle: "Apresentar um Negócio | Verde Capital",
      ptDescription:
        "Envie uma oportunidade de desenvolvimento diretamente à equipa da Verde Capital. Subscrevemos, estruturamos e financiamos — a comissão é sua.",
      nlTitle: "Deal indienen | Verde Capital",
      nlDescription:
        "Stuur een woningbouwontwikkeling rechtstreeks naar het team van Verde Capital. Wij beoordelen, structureren en financieren — de provisie is voor u.",
    },
    "/investors": {
      title: "Invest in Portuguese Property Loans | Verde Capital",
      description:
        "Lend alongside Verde Capital at an 8% annual yield, paid quarterly — senior-secured on Portuguese residential real estate, from a €100,000 ticket.",
      ptTitle: "Invista em Crédito Imobiliário em Portugal | Verde Capital",
      ptDescription:
        "Emprestie juntamente com a Verde Capital a uma rentabilidade anual de 8%, paga trimestralmente — com garantia sénior sobre imobiliário residencial em Portugal, a partir de €100.000.",
      nlTitle: "Investeren in Portugese vastgoedleningen | Verde Capital",
      nlDescription:
        "Leen samen met Verde Capital aan 8% jaarrendement, per kwartaal uitgekeerd — senior gedekt op Portugese woningen, vanaf een ticket van €100.000.",
    },
    "/expert-network": {
      title: "Expert Network | Verde Capital",
      description:
        "Local planning, legal, technical, valuation and market specialists behind every Verde Capital credit decision in Portugal. Apply to join the network.",
      ptTitle: "Rede de Especialistas | Verde Capital",
      ptDescription:
        "Especialistas locais de planeamento, legal, técnico, avaliação e mercado por trás de cada decisão de crédito da Verde Capital em Portugal. Candidate-se.",
      nlTitle: "Expertennetwerk | Verde Capital",
      nlDescription:
        "Lokale specialisten in ruimtelijke ordening, juridisch, techniek, taxatie en markt achter elke kredietbeslissing van Verde Capital in Portugal. Solliciteer om deel te nemen.",
    },
    "/about": {
      title: "About Verde Capital | Development Finance in Portugal",
      description:
        "Real estate, finance and AI expertise under one roof — who we are and how Verde Capital underwrites residential development in Portugal.",
      ptTitle: "Sobre a Verde Capital | Financiamento ao Desenvolvimento em Portugal",
      ptDescription:
        "Experiência imobiliária, financeira e de IA sob o mesmo teto — quem somos e como a Verde Capital subscreve o desenvolvimento residencial em Portugal.",
      nlTitle: "Over Verde Capital | Ontwikkelfinanciering in Portugal",
      nlDescription:
        "Vastgoed-, financieel- en AI-expertise onder één dak — wie wij zijn en hoe Verde Capital residentiële ontwikkeling in Portugal beoordeelt.",
    },
    "/contact": {
      title: "Contact Verde Capital | Portugal",
      description:
        "Talk to the Verde Capital team — call, WhatsApp or send a message about financing, investing, brokering or joining the expert network.",
      ptTitle: "Contacto Verde Capital | Portugal",
      ptDescription:
        "Fale com a equipa da Verde Capital — ligue, WhatsApp ou envie uma mensagem sobre financiamento, investimento, intermediação ou a rede de especialistas.",
      nlTitle: "Contact Verde Capital | Portugal",
      nlDescription:
        "Neem contact op met het team van Verde Capital — bel, WhatsApp of stuur een bericht over financiering, beleggen, tussenbemiddeling of het expertennetwerk.",
    },
    "/book": {
      title: "Book a Meeting | Verde Capital",
      description:
        "Pick a day and time to meet Verde Capital — confirmed instantly when the slot is free. Prefer to talk? Call or WhatsApp us directly.",
      ptTitle: "Marcar uma Reunião | Verde Capital",
      ptDescription:
        "Escolha o dia e a hora para conhecer a Verde Capital — confirmação instantânea quando o horário está livre. Prefere falar? Ligue-nos ou envie WhatsApp.",
      nlTitle: "Afspraak maken | Verde Capital",
      nlDescription:
        "Kies een dag en tijd om Verde Capital te ontmoeten — direct bevestigd wanneer het tijdvak vrij is. Liever bellen? Bel of app ons.",
    },
    "/faq": {
      title: "Frequently Asked Questions | Verde Capital",
      description:
        "Answers for developers, investors and brokers — financing stages, indicative terms, security, the 8% quarterly yield and broker commissions.",
      ptTitle: "Perguntas Frequentes | Verde Capital",
      ptDescription:
        "Respostas para promotores, investidores e intermediários — fases de financiamento, condições indicativas, garantias, a rentabilidade de 8% trimestral e comissões de intermediação.",
      nlTitle: "Veelgestelde vragen | Verde Capital",
      nlDescription:
        "Antwoorden voor ontwikkelaars, investeerders en tussenpersonen — financieringsfasen, indicatieve voorwaarden, zekerheden, het kwartaalrendement van 8% en provisies.",
    },
    "/sitemap": {
      title: "Sitemap | Verde Capital",
      description:
        "Every page of the Verde Capital website in one place — products, company information, partner pages and legal documents.",
      ptTitle: "Mapa do Site | Verde Capital",
      ptDescription:
        "Todas as páginas do site da Verde Capital num só lugar — produtos, empresa, parceiros e documentos legais.",
      nlTitle: "Sitemap | Verde Capital",
      nlDescription:
        "Alle pagina's van de Verde Capital-website op één plek — producten, bedrijfsinformatie, partnerpagina's en juridische documenten.",
    },
    "/privacy": {
      title: "Privacy Policy | Verde Capital",
      description: "How Verde Capital collects, uses and protects your personal data.",
      ptTitle: "Política de Privacidade | Verde Capital",
      ptDescription: "Como a Verde Capital recolhe, utiliza e protege os seus dados pessoais.",
    },
    "/terms": {
      title: "Terms of Use | Verde Capital",
      description: "The terms of use for the Verde Capital website.",
      ptTitle: "Termos de Utilização | Verde Capital",
      ptDescription: "Os termos de utilização do site da Verde Capital.",
    },
    "/cookies": {
      title: "Cookies Policy | Verde Capital",
      description: "The cookies the Verde Capital website sets and why.",
      ptTitle: "Política de Cookies | Verde Capital",
      ptDescription: "Os cookies que o site da Verde Capital utiliza e porquê.",
    },
    "/lending-disclaimer": {
      title: "Lending Disclaimer | Verde Capital",
      description: "The lending disclaimer for the Verde Capital website.",
      ptTitle: "Aviso Legal sobre Crédito | Verde Capital",
      ptDescription: "O aviso legal sobre crédito do site da Verde Capital.",
    },
    "/lp/finance": {
      title: "Development Finance Portugal — Indicative Terms in Days | Verde Capital",
      description: "Senior-secured development finance of €1–5m. Indicative terms within 24 hours.",
      ptTitle: "Financiamento ao Desenvolvimento — Condições em Dias | Verde Capital",
      ptDescription: "Financiamento com garantia sénior de €1–5m. Condições indicativas em 24 horas.",
    },
    "/lp/pre-construction": {
      title: "Pre-Construction Finance Portugal — Land, Licensing, Pre-Sales | Verde Capital",
      description: "Bridge land acquisition, licensing and pre-sales — then refinance with your bank.",
      ptTitle: "Financiamento à Pré-Construção — Terreno, Licenciamento | Verde Capital",
      ptDescription: "Compense terreno, licenciamento e pré-vendas — depois refinancie com o banco.",
    },
    "/lp/investors": {
      title: "Lend Alongside Verde — 8% p.a. Secured on Portuguese Property",
      description: "8% per annum paid quarterly, first-ranking mortgage security. Request the investor pack.",
      ptTitle: "Invista ao Lado da Verde — 8% ao Ano com Garantia",
      ptDescription: "8% ao ano pago trimestralmente, com hipoteca de primeiro ranking. Peça a informação.",
    },
  };
  const known = Boolean(ROUTE_META[base]);
  let meta = known ? ROUTE_META[base] : ROUTE_META["/"];
  const insightSlug = base.startsWith("/insights/") ? base.split("/")[2] : null;
  const insight = insightSlug ? ARTICLE_META[insightSlug] : null;
  if (insight) {
    meta = { title: insight.en.title, description: insight.en.description, ptTitle: insight.pt.title, ptDescription: insight.pt.description };
  }
  const title = lang === "pt" ? meta.ptTitle : lang === "nl" ? meta.nlTitle || meta.title : meta.title;
  const description =
    lang === "pt" ? meta.ptDescription : lang === "nl" ? meta.nlDescription || meta.description : meta.description;
  return (
    <Seo
      title={title}
      description={description}
      path={known || insight ? pathname : lang === "pt" ? "/pt" : lang === "nl" ? "/nl" : "/"}
      lang={lang}
    />
  );
}

function SiteNavbar() {
  const { pathname } = useLocation();
  const p = pathname.replace(/^\/(pt|nl)(?=\/|$)/, "");
  if (p.startsWith("/apply") || p.startsWith("/book") || p.startsWith("/lp/")) return null;
  return <Navbar />;
}

function SiteFooter() {
  const { pathname } = useLocation();
  const p = pathname.replace(/^\/(pt|nl)(?=\/|$)/, "");
  if (p.startsWith("/lp/")) return null;
  return <Footer />;
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <LangProvider>
          <SmoothScroll />
          <ScrollToTop />
          <RouteMeta />
          <SiteNavbar />
          <main>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/products/pre-construction-finance" element={<PreConstructionFinance />} />
              <Route path="/apply" element={<Apply />} />
              <Route path="/brokers" element={<Brokers />} />
              <Route path="/brokers/submit" element={<BrokerSubmit />} />
              <Route path="/expert-network" element={<ExpertNetwork />} />
              <Route path="/investors" element={<Investors />} />
              <Route path="/investor-info" element={<InvestorInfo />} />
              <Route path="/development-finance-portugal" element={<DevelopmentFinance />} />
              <Route path="/insights" element={<Insights />} />
              <Route path="/insights/:slug" element={<ArticlePage />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/admin" element={<AdminInbox />} />
              <Route path="/lp/finance" element={<LandingPage variant="finance" />} />
              <Route path="/lp/pre-construction" element={<LandingPage variant="pre-construction" />} />
              <Route path="/lp/investors" element={<LandingPage variant="investors" />} />
              <Route path="/pt/lp/finance" element={<LandingPage variant="finance" />} />
              <Route path="/pt/lp/pre-construction" element={<LandingPage variant="pre-construction" />} />
              <Route path="/pt/lp/investors" element={<LandingPage variant="investors" />} />
              <Route path="/book" element={<Book />} />
              <Route path="/privacy" element={<Legal doc="privacy" />} />
              <Route path="/cookies" element={<Legal doc="cookies" />} />
              <Route path="/terms" element={<Legal doc="terms" />} />
              <Route path="/lending-disclaimer" element={<Legal doc="lending" />} />
              <Route path="/faq" element={<Faq />} />
              <Route path="/sitemap" element={<Sitemap />} />
              <Route path="*" element={<Home />} />
              <Route path="/pt" element={<Home />} />
              <Route path="/pt/products/pre-construction-finance" element={<PreConstructionFinance />} />
              <Route path="/pt/apply" element={<Apply />} />
              <Route path="/pt/brokers" element={<Brokers />} />
              <Route path="/pt/brokers/submit" element={<BrokerSubmit />} />
              <Route path="/pt/expert-network" element={<ExpertNetwork />} />
              <Route path="/pt/investors" element={<Investors />} />
              <Route path="/pt/investor-info" element={<InvestorInfo />} />
              <Route path="/pt/development-finance-portugal" element={<DevelopmentFinance />} />
              <Route path="/pt/insights" element={<Insights />} />
              <Route path="/pt/insights/:slug" element={<ArticlePage />} />
              <Route path="/pt/about" element={<About />} />
              <Route path="/pt/contact" element={<Contact />} />
              <Route path="/pt/book" element={<Book />} />
              <Route path="/pt/privacy" element={<Legal doc="privacy" />} />
              <Route path="/pt/cookies" element={<Legal doc="cookies" />} />
              <Route path="/pt/terms" element={<Legal doc="terms" />} />
              <Route path="/pt/lending-disclaimer" element={<Legal doc="lending" />} />
              <Route path="/pt/faq" element={<Faq />} />
              <Route path="/pt/sitemap" element={<Sitemap />} />
              <Route path="/nl" element={<Home />} />
              <Route path="/nl/products/pre-construction-finance" element={<PreConstructionFinance />} />
              <Route path="/nl/apply" element={<Apply />} />
              <Route path="/nl/brokers" element={<Brokers />} />
              <Route path="/nl/brokers/submit" element={<BrokerSubmit />} />
              <Route path="/nl/expert-network" element={<ExpertNetwork />} />
              <Route path="/nl/investors" element={<Investors />} />
              <Route path="/nl/investor-info" element={<InvestorInfo />} />
              <Route path="/nl/development-finance-portugal" element={<DevelopmentFinance />} />
              <Route path="/nl/insights" element={<Insights />} />
              <Route path="/nl/insights/:slug" element={<ArticlePage />} />
              <Route path="/nl/about" element={<About />} />
              <Route path="/nl/contact" element={<Contact />} />
              <Route path="/nl/book" element={<Book />} />
              <Route path="/nl/privacy" element={<Legal doc="privacy" />} />
              <Route path="/nl/cookies" element={<Legal doc="cookies" />} />
              <Route path="/nl/terms" element={<Legal doc="terms" />} />
              <Route path="/nl/lending-disclaimer" element={<Legal doc="lending" />} />
              <Route path="/nl/faq" element={<Faq />} />
              <Route path="/nl/sitemap" element={<Sitemap />} />
            </Routes>
          </main>
          <SiteFooter />
        </LangProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
